/**
 * A page-level notice that stays until the state it describes changes or the person dismisses it:
 * a licence under review, a payment past due, a saved draft not yet sent. Anything that happened
 * a moment ago and needs no decision is a `Toast`, not a banner.
 *
 * Every tone carries an icon and a word, so colour is never the only cue. The word defaults to
 * "Note", "Done", "Warning" or "Problem" and can be replaced with `title`. Tones use the `*Subtle`
 * fill and `*Text` colour, which meet 4.5:1 together. A `danger` banner is an alert and is read at
 * once. The others are polite status text.
 *
 * `onDismiss` adds a close button, for a notice that should clear (a "Saved" that otherwise stays
 * above the next screen). `action` is one button or link beside the words.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { CheckIcon, CloseIcon, InfoIcon, WarningIcon } from "./icons.tsx";
import { IconButton } from "./IconButton.tsx";
import { textStyle } from "./textStyle.ts";
const tones = {
  info: {
    kWkggS: "x9w37u4",
    kMwMTN: "xzjdp0e",
    $$css: true
  },
  ok: {
    kWkggS: "xl5rrbb",
    kMwMTN: "xdhxsyf",
    $$css: true
  },
  warn: {
    kWkggS: "x1n3p3y5",
    kMwMTN: "x1j243yh",
    $$css: true
  },
  danger: {
    kWkggS: "xqsqr6",
    kMwMTN: "x6ygkrl",
    $$css: true
  }
};
const styles = {
  banner: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kwnvtZ: "x1a02dak",
    kOIVth: "x626il7",
    k8WAf4: "xp1t01x",
    kg3NbH: "x3i8z2n",
    kMzoRj: "xdq7b4a",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x181nr7w",
    kaIpWk: "x1fbwzx",
    $$css: true
  },
  word: {
    k63SB2: "xqu410z",
    kGuDYH: "x1qlqyl8",
    $$css: true
  }
};
export type BannerTone = keyof typeof tones;
const WORDS = {
  info: "Note",
  ok: "Done",
  warn: "Warning",
  danger: "Problem"
} as const;
const ICONS = {
  info: InfoIcon,
  ok: CheckIcon,
  warn: WarningIcon,
  danger: WarningIcon
} as const;
export interface BannerProps {
  readonly tone?: BannerTone;
  /** The bold word before the message. Defaults from the tone. */
  readonly title?: string;
  readonly action?: ReactNode;
  readonly onDismiss?: () => void;
  /** Names the dismiss button. Two banners on one page need different ones: "Dismiss saved note". */
  readonly dismissLabel?: string;
  readonly testId?: string;
  readonly children: ReactNode;
}
export function Banner({
  tone = "info",
  title,
  action,
  onDismiss,
  dismissLabel = "Dismiss",
  testId,
  children
}: BannerProps) {
  const Icon = ICONS[tone];
  return <div role={tone === "danger" ? "alert" : "status"} data-testid={testId} {...stylex.props(textStyle.body, styles.banner, tones[tone])}>
      <Icon />
      <p className="x1iyjqo2 xz35qm7 xeuugli x1ghz6dp xj0a0fe x1fzhlzt">
        <strong {...stylex.props(textStyle.label, styles.word)}>{title ?? WORDS[tone]}.</strong>{" "}
        {children}
      </p>
      {action !== undefined && <div className="x2lah0s">{action}</div>}
      {onDismiss !== undefined && <span className="x2lah0s xamitd3">
          <IconButton label={dismissLabel} icon={<CloseIcon />} onClick={onDismiss} tipAlign="end" />
        </span>}
    </div>;
}